import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { isAfter } from 'date-fns';
import { AppView, CurrencyAmountText, Spacer } from 'design-system';
import BottomPanel from 'design-system/BottomPanel';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import EmojiText from 'design-system/EmojiText';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconStatusDone from 'design-system/icons/status/IconStatusDone';
import IconStatusPending from 'design-system/icons/status/IconStatusPending';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import Squircle from 'design-system/Squircle';
import Text from 'design-system/Text';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import CollectingFromBankCard from 'features/pots/components/AutosaveActivityTimeline/CollectingFromBankCard';
import { getPotDisplayName } from 'features/pots/utils';
import createStyleSheets from 'utils/createStyleSheets';
import { isWeb } from '../../../../constants';
import ArrivingInEmmaInfoCard from '../../components/AutosaveActivityTimeline/ArrivingInEmmaInfoCard';
import InitiatedInfoCard from '../../components/AutosaveActivityTimeline/InitiatedInfoCard';
const AutosaveActivityTimeline = ({ route, navigation }) => {
    const { activity } = route.params;
    const pot = 'pot' in route.params ? route.params.pot : undefined;
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const isPotActivity = 'potAmount' in activity;
    const contentContainerStyle = useMarginBottom('paddingBottom', isPotActivity ? SCREEN_BOTTOM_PADDING : HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const goBack = useCallback(() => {
        navigation.pop();
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton onPress={goBack} inWebHeader/>,
        });
    }, [goBack]);
    const info = useMemo(() => {
        const { paymentCollectionDate } = activity;
        const hasCollectedPayment = isAfter(new Date(), new Date(paymentCollectionDate));
        return {
            rules: activity.savingsPlanInfo.rules,
            savingsPlanInfo: activity.savingsPlanInfo,
            initiatedCardTimeStamp: activity.initiatedAt,
            isPending: activity.status === 'PENDING',
            completed: activity.status === 'COMPLETED',
            fee: activity.savingsPlanInfo.fee,
            hasCollectedPayment,
            paymentCollectionDate,
            completedDate: activity.completedAt || activity.completedEta,
        };
    }, [activity]);
    const getStatusName = useMemo(() => {
        if ('status' in activity) {
            switch (activity.status) {
                case 'PENDING':
                    return ' pending';
                case 'COMPLETED':
                    return ' completed';
                default:
                    return '';
            }
        }
        return '';
    }, [activity]);
    const title = useMemo(() => {
        const statusName = getStatusName;
        if (isPotActivity) {
            return `Part of${statusName ? ` a${statusName}` : ''} Autosave`;
        }
        return `Autosave${getStatusName}`;
    }, [getStatusName, isPotActivity]);
    const viewFullAutosave = useCallback(() => {
        if (isPotActivity) {
            const { potAmount: _potAmount, potId: _potId, ...finalActivity } = activity;
            navigation.push('AutosaveActivityTimeline', {
                activity: finalActivity,
            });
        }
    }, [activity, isPotActivity]);
    const amount = isPotActivity ? activity.potAmount : activity.amount;
    return (<AppView avoidHeader withFrame={false}>
      <ScrollView contentContainerStyle={contentContainerStyle}>
        <View style={styles.iconWrapper}>
          <View>
            <View style={styles.iconContainer}>
              <View style={styles.iconContainerInner}>
                <IconRocket colorKey="brand"/>
              </View>
            </View>
            <View style={styles.floatingStatusIconContainer}>
              <Squircle height={rem(24)} width={rem(24)} color={colors.background.dark}>
                {'status' in activity && activity.status === 'COMPLETED' ? (<View testID="completedIcon">
                    <IconStatusDone colorKey="positive"/>
                  </View>) : (<View testID="pendingIcon">
                    <IconStatusPending color={colors.cards.onDark} bgColor={colors.elements.statusInformational}/>
                  </View>)}
              </Squircle>
            </View>
          </View>
        </View>
        <View style={styles.infoContainer}>
          <Text Text-14 Secondary centered>
            {title}
          </Text>
          <CurrencyAmountText size="XL" value={{ amount: amount.value, currency: amount.currency }}/>
          {isPotActivity && !!pot ? (<View style={styles.potInfoContainer}>
              <View style={styles.emojiContainer}>
                <EmojiText fontSize={rem(10)}>{pot.emoji}</EmojiText>
              </View>
              <Spacer w={8}/>
              <Text TextThin-14 Secondary>
                {getPotDisplayName(pot.name)}
              </Text>
            </View>) : null}
        </View>
        <Spacer h={8}/>
        <View>
          <InitiatedInfoCard fee={info.fee} rules={info.rules} timeStamp={info.initiatedCardTimeStamp} isForPot={isPotActivity}/>
          <CollectingFromBankCard hasCollectedPayment={info.hasCollectedPayment} paymentCollectionDate={info.paymentCollectionDate}/>
          <ArrivingInEmmaInfoCard potName={pot?.name} completed={info.completed} completedDate={info.completedDate} savingsPlanInfo={info.savingsPlanInfo} isActiveStep={!info.completed && info.hasCollectedPayment}/>
        </View>
      </ScrollView>
      {isPotActivity ? (<BottomPanel>
          <Button brandReversed title="Close" onPress={goBack}/>
          <Spacer h={8}/>
          <Button brand title="View full Autosave" onPress={viewFullAutosave}/>
        </BottomPanel>) : (<Button floating brand title="Close" onPress={goBack}/>)}
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    iconWrapper: {
        alignItems: 'center',
    },
    iconContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    iconContainerInner: {
        width: rem(52),
        height: rem(52),
        borderRadius: rem(52),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.brandLight,
    },
    floatingStatusIconContainer: {
        right: 0,
        bottom: 0,
        width: rem(24),
        height: rem(24),
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
    },
    floatingStatusIconInnerContainer: {
        width: rem(20),
        height: rem(20),
        alignItems: 'center',
        justifyContent: 'center',
    },
    infoContainer: {
        paddingTop: rem(24),
        alignItems: 'center',
    },
    potInfoContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
    },
    emojiContainer: {
        width: rem(18),
        height: rem(18),
        borderWidth: rem(1),
        alignItems: 'center',
        borderRadius: rem(9),
        justifyContent: 'center',
        borderColor: colors.borders.divider,
        backgroundColor: colors.cards.onDark,
    },
}));
export default AutosaveActivityTimeline;
